import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import html2pdf from "html2pdf.js";
import {
  Download,
  X,
  FileText,
  Package,
  Calendar,
  MapPin,
  Phone,
  Mail,
  Building2,
  CreditCard,
  Truck,
  Save,
  CheckCircle,
} from "lucide-react";

const PurchaseOrderForm = ({
  isOpen,
  onClose,
  cartItems,
  onSubmit,
  onConfirmPO,
  onSaveDraft,
  customerInfo,
  draftOrder = null, // Add support for editing existing drafts
  status = "Draft",
  viewOnly = false, // Add view-only mode
  companyInfo = {
    name: "Axia B2B Solutions",
    address: "123 Business Park, Suite 100",
    city: "Tech City, TC 12345",
    phone: "+1 (555) 123-4567",
    email: "orders@ennovaceb2b.com",
    website: "www.ennovaceb2b.com",
    taxId: "TAX-123456789",
    poPrefix: "PO-",
  },
}) => {
  const [formData, setFormData] = useState({
    poNumber: "",
    orderDate: format(new Date(), "yyyy-MM-dd"),
    requiredDate: "",
    deliveryAddress: {
      name: customerInfo?.companyName || "",
      address: customerInfo?.address || "",
      city: customerInfo?.city || "",
      state: customerInfo?.state || "",
      zipCode: customerInfo?.zipCode || "",
      country: customerInfo?.country || "USA",
      phone: customerInfo?.phone || "",
      email: customerInfo?.email || "",
    },
    billingAddress: {
      name: customerInfo?.companyName || "",
      address: customerInfo?.address || "",
      city: customerInfo?.city || "",
      state: customerInfo?.state || "",
      zipCode: customerInfo?.zipCode || "",
      country: customerInfo?.country || "USA",
      phone: customerInfo?.phone || "",
      email: customerInfo?.email || "",
    },
    items: [],
    subtotal: 0,
    taxRate: 8.5,
    taxAmount: 0,
    shippingCost: 0,
    discount: 0,
    total: 0,
    paymentTerms: "Net 30",
    shippingMethod: "Standard Ground",
    specialInstructions: "",
    notes: "",
    authorizedBy: customerInfo?.name || "",
    authorizedDate: format(new Date(), "yyyy-MM-dd"),
    authorizedSignature: "",
    termsAndConditions: `1. All prices are subject to change without notice.
2. Payment terms are Net 30 days from invoice date.
3. Late payments are subject to 1.5% monthly interest.
4. Returns must be made within 30 days with prior authorization.
5. Freight charges are additional unless otherwise specified.
6. This purchase order is subject to our standard terms and conditions.`,
  });

  // Initialize form data from draft order if editing
  useEffect(() => {
    if (draftOrder) {
      // Parse addresses from JSON strings if they exist
      let deliveryAddress = formData.deliveryAddress;
      let billingAddress = formData.billingAddress;

      try {
        if (draftOrder.deliveryAddress) {
          deliveryAddress =
            typeof draftOrder.deliveryAddress === "string"
              ? JSON.parse(draftOrder.deliveryAddress)
              : draftOrder.deliveryAddress;
        }
        if (draftOrder.billingAddress) {
          billingAddress =
            typeof draftOrder.billingAddress === "string"
              ? JSON.parse(draftOrder.billingAddress)
              : draftOrder.billingAddress;
        }
      } catch (e) {
        console.warn("Error parsing address data:", e);
      }

      const items =
        draftOrder.orderItems?.map((item) => ({
          id: item.id,
          productSkuId: item.productSkuId,
          productSkuId: item.productSkuId,
          name:
            item.productSku?.name ||
            item.productSku?.description ||
            item.productSku?.product?.description ||
            item.product?.description ||
            item.product?.name ||
            "",
          sku: item.productSku?.sku || item.product?.itemCode || "",
          unitPrice: item.price || item.unitPrice || 0,
          quantity: item.quantity || 1,
          gstPercent:
            item.productSku?.product?.gstPercent ||
            item.product?.gstPercent ||
            18, // Default 18% GST
          lineTotal: (item.price || item.unitPrice || 0) * (item.quantity || 1),
        })) || [];

      setFormData({
        poNumber:
          draftOrder.poNumber ||
          draftOrder.purchaseOrderNumber ||
          draftOrder.orderNumber ||
          "",
        orderDate: draftOrder.createdAt
          ? format(new Date(draftOrder.createdAt), "yyyy-MM-dd")
          : format(new Date(), "yyyy-MM-dd"),
        requiredDate: draftOrder.expectedDeliveryDate
          ? format(new Date(draftOrder.expectedDeliveryDate), "yyyy-MM-dd")
          : "",
        deliveryAddress,
        billingAddress,
        items,
        subtotal: draftOrder.subtotal || 0,
        taxRate: 8.5, // Default tax rate
        taxAmount: draftOrder.taxAmount || 0,
        shippingCost: draftOrder.shippingAmount || 0,
        discount: draftOrder.discountAmount || 0,
        total: draftOrder.totalAmount || 0,
        paymentTerms: draftOrder.paymentTerms || "Net 30",
        shippingMethod: "Standard Ground",
        specialInstructions:
          draftOrder.deliveryInstructions ||
          draftOrder.specialInstructions ||
          "",
        notes: draftOrder.customerNotes || draftOrder.notes || "",
        authorizedBy: draftOrder.authorizedBy || customerInfo?.name || "",
        authorizedDate: draftOrder.authorizedDate
          ? format(new Date(draftOrder.authorizedDate), "yyyy-MM-dd")
          : format(new Date(), "yyyy-MM-dd"),
        authorizedSignature: draftOrder.authorizedSignature || "",
        termsAndConditions:
          draftOrder.termsAndConditions || formData.termsAndConditions,
      });
    }
  }, [draftOrder, customerInfo]);

  useEffect(() => {
    if (cartItems && cartItems.length > 0 && !draftOrder) {
      const items = cartItems.map((item) => ({
        ...item,
        productSkuId: item.productSkuId,
        name:
          item.productSku?.name ||
          item.productSku?.description ||
          item.productSku?.product?.description ||
          item.product?.description ||
          item.productSku?.product?.name ||
          item.product?.name ||
          item.name ||
          "",
        sku: item.productSku?.sku || item.product?.itemCode || "",
        itemCode:
          item.productSku?.product?.itemCode || item.product?.itemCode || "",
        unitPrice: item.price || 0,
        quantity: item.quantity || 1,
        gstPercent:
          item.productSku?.product?.gstPercent ||
          item.product?.gstPercent ||
          18, // Default 18% GST
        lineTotal: (item.price || 0) * (item.quantity || 1),
      }));

      // Calculate GST at item level and include in subtotal
      let subtotal = 0;
      let totalGST = 0;

      items.forEach((item) => {
        const lineSubtotal = item.unitPrice * item.quantity;
        const gstAmount = (lineSubtotal * item.gstPercent) / 100;
        const lineTotal = lineSubtotal + gstAmount;

        item.lineTotal = lineTotal; // Line total now includes GST
        subtotal += lineTotal; // Subtotal includes GST
        totalGST += gstAmount;
      });

      const total = subtotal + formData.shippingCost - formData.discount;

      setFormData((prev) => ({
        ...prev,
        items,
        subtotal,
        taxAmount: totalGST,
        total,
      }));
    }
  }, [cartItems, formData.shippingCost, formData.discount, draftOrder]);

  // Update addresses when customerInfo changes (only if not editing a draft)
  useEffect(() => {
    if (!draftOrder) {
      setFormData((prev) => ({
        ...prev,
        deliveryAddress: {
          name: customerInfo?.companyName || "",
          address: customerInfo?.billAddress || customerInfo?.address || "",
          city: customerInfo?.billCity || customerInfo?.city || "",
          state: customerInfo?.billState || customerInfo?.state || "",
          zipCode: customerInfo?.billZipCode || customerInfo?.zipCode || "",
          country:
            customerInfo?.billCountry || customerInfo?.country || "India",
          phone: customerInfo?.phone || "",
          email: customerInfo?.email || "",
        },
        billingAddress: {
          name: customerInfo?.companyName || "",
          address: customerInfo?.billAddress || customerInfo?.address || "",
          city: customerInfo?.billCity || customerInfo?.city || "",
          state: customerInfo?.billState || customerInfo?.state || "",
          zipCode: customerInfo?.billZipCode || customerInfo?.zipCode || "",
          country:
            customerInfo?.billCountry || customerInfo?.country || "India",
          phone: customerInfo?.phone || "",
          email: customerInfo?.email || "",
        },
      }));
    }
  }, [customerInfo, draftOrder]);

  useEffect(() => {
    // Generate PO number only if not editing a draft and no PO number exists
    if (!draftOrder && !formData.poNumber) {
      const currentYear = new Date().getFullYear();
      const customerId = customerInfo?.id || "XXX";
      const timestamp = Date.now().toString().slice(-4); // Last 4 digits of timestamp
      const poNumber = `PO-${customerId}-${currentYear}-${timestamp}`;
      setFormData((prev) => ({ ...prev, poNumber }));
    }
  }, [companyInfo.poPrefix, draftOrder, formData.poNumber, customerInfo]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddressChange = (type, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value,
      },
    }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = {
      ...newItems[index],
      [field]: value,
    };

    // Recalculate line total with GST
    if (field === "quantity" || field === "unitPrice") {
      const item = newItems[index];
      const lineSubtotal = item.unitPrice * item.quantity;
      const gstAmount = (lineSubtotal * (item.gstPercent || 18)) / 100;
      item.lineTotal = lineSubtotal + gstAmount;
    }

    // Calculate totals with GST included
    let subtotal = 0;
    let totalGST = 0;

    newItems.forEach((item) => {
      const lineSubtotal = item.unitPrice * item.quantity;
      const gstAmount = (lineSubtotal * (item.gstPercent || 18)) / 100;
      const lineTotal = lineSubtotal + gstAmount;

      item.lineTotal = lineTotal;
      subtotal += lineTotal;
      totalGST += gstAmount;
    });

    const total = subtotal + formData.shippingCost - formData.discount;

    setFormData((prev) => ({
      ...prev,
      items: newItems,
      subtotal,
      taxAmount: totalGST,
      total,
    }));
  };

  const handleCalculationChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    let newTotal = formData.total;

    if (field === "shippingCost" || field === "discount") {
      newTotal =
        formData.subtotal +
        formData.taxAmount +
        (field === "shippingCost" ? numValue : formData.shippingCost) -
        (field === "discount" ? numValue : formData.discount);
    }

    setFormData((prev) => ({
      ...prev,
      [field]: numValue,
      total: newTotal,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleConfirmPO = (e) => {
    e.preventDefault();
    onConfirmPO(formData);
  };

  const handleSaveDraft = (e) => {
    e.preventDefault();
    onSaveDraft(formData);
  };

  const copyDeliveryToBilling = () => {
    setFormData((prev) => ({
      ...prev,
      billingAddress: { ...prev.deliveryAddress },
    }));
  };

  const handleDownloadPDF = () => {
    const element = document.getElementById("po-pdf-content");
    if (element) {
      // Create a wrapper for PDF generation
      const wrapper = document.createElement("div");
      wrapper.className = "page-container";

      // Add custom print styles
      const pageStyle = document.createElement("style");
      pageStyle.innerHTML = `
        body { 
          margin: 0; 
          padding: 0; 
          font-family: Arial, Helvetica, sans-serif;
          font-size: 8px;
          line-height: 1.2;
          color: #000;
          background: white;
        }
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        #po-pdf-content {
          background: white !important;
          padding: 0 !important;
          margin: 0 !important;
        }
        .po-header {
          background: white !important;
          border-bottom: 3px solid #2563eb !important;
          padding: 8px 12px !important;
          margin-bottom: 8px !important;
        }
        .po-header h1 {
          font-size: 16px !important;
          margin: 0 !important;
        }
        .po-header h2 {
          font-size: 20px !important;
          margin: 0 0 4px 0 !important;
        }
        .grid { display: grid !important; }
        .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        .lg\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        .lg\\:grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
        .gap-2, .gap-3, .gap-4 { gap: 8px !important; }
        .px-4, .px-6 { padding-left: 12px !important; padding-right: 12px !important; }
        .py-2, .py-3, .py-4 { padding-top: 8px !important; padding-bottom: 8px !important; }
        .p-3, .p-4 { padding: 8px !important; }
        .mb-2, .mb-3 { margin-bottom: 6px !important; }
        .mt-2, .mt-4 { margin-top: 6px !important; }
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 6px 0 !important;
          page-break-inside: auto;
          font-size: 8px !important;
        }
        tr {
          page-break-inside: avoid;
          page-break-after: auto;
        }
        th {
          background-color: #2563eb !important;
          color: white !important;
          padding: 4px 3px !important;
          font-weight: bold !important;
          border: 1px solid #1e40af !important;
          font-size: 8px !important;
        }
        td {
          border: 1px solid #d1d5db !important;
          padding: 3px 3px !important;
          color: #000 !important;
          font-size: 8px !important;
        }
        .bg-blue-600, .dark\\:bg-blue-700 {
          background-color: #2563eb !important;
          color: white !important;
        }
        .bg-blue-50, .dark\\:bg-blue-900\\/20 {
          background-color: #eff6ff !important;
        }
        .bg-indigo-50, .dark\\:bg-indigo-900\\/20 {
          background-color: #eef2ff !important;
        }
        .bg-white, .dark\\:bg-gray-800, .dark\\:bg-gray-900 {
          background-color: white !important;
        }
        .bg-gray-50, .dark\\:bg-gray-900 {
          background-color: #f9fafb !important;
        }
        .border-blue-600, .dark\\:border-blue-800 {
          border-color: #2563eb !important;
        }
        .border-blue-100, .dark\\:border-blue-900 {
          border-color: #dbeafe !important;
        }
        .border-indigo-100, .dark\\:border-indigo-900 {
          border-color: #e0e7ff !important;
        }
        .border-gray-300, .dark\\:border-gray-600 {
          border-color: #d1d5db !important;
        }
        .text-blue-600, .text-blue-700, .dark\\:text-blue-400 {
          color: #2563eb !important;
        }
        .text-indigo-600, .text-indigo-700, .dark\\:text-indigo-400 {
          color: #4f46e5 !important;
        }
        .text-green-600, .dark\\:text-green-400 {
          color: #16a34a !important;
        }
        .text-gray-900, .dark\\:text-white {
          color: #111827 !important;
        }
        .text-gray-600, .text-gray-700, .dark\\:text-gray-300, .dark\\:text-gray-400 {
          color: #4b5563 !important;
        }
        .text-white {
          color: white !important;
        }
        .text-blue-100 {
          color: #1e40af !important;
        }
        .text-xs, .text-sm {
          font-size: 8px !important;
        }
        .text-base {
          font-size: 10px !important;
        }
        .text-lg {
          font-size: 12px !important;
        }
        .text-xl {
          font-size: 14px !important;
        }
        .text-2xl {
          font-size: 16px !important;
        }
        .text-3xl {
          font-size: 18px !important;
        }
        svg {
          display: inline-block;
          width: 12px !important;
          height: 12px !important;
        }
        .w-4, .h-4 { width: 12px !important; height: 12px !important; }
        .w-5, .h-5 { width: 14px !important; height: 14px !important; }
        .w-12, .h-12 { width: 32px !important; height: 32px !important; }
        .rounded-xl, .rounded-lg { border-radius: 4px !important; }
        .shadow-lg, .shadow-sm { box-shadow: none !important; }
        .space-y-2 > * + * { margin-top: 4px !important; }
        .space-y-3 > * + * { margin-top: 6px !important; }
        @page {
          size: A4;
          margin: 8mm;
        }
      `;

      // Clone the PDF content area
      const clonedElement = element.cloneNode(true);

      // Create content wrapper
      const contentDiv = document.createElement("div");
      contentDiv.appendChild(pageStyle);
      contentDiv.appendChild(clonedElement);

      html2pdf()
        .from(contentDiv)
        .set({
          margin: [8, 8, 8, 8], // Top, Left, Bottom, Right (mm)
          filename: `${formData.poNumber || "PO"}.pdf`,
          image: {
            type: "jpeg",
            quality: 0.95,
          },
          html2canvas: {
            scale: 1.5,
            useCORS: true,
            letterRendering: true,
            logging: false,
            windowWidth: 800,
            scrollY: 0,
          },
          jsPDF: {
            unit: "mm",
            format: "a4",
            orientation: "portrait",
            compress: true,
          },
          pagebreak: { mode: ["avoid-all", "css", "legacy"] },
        })
        .save();
    }
  };

  if (!isOpen) return null;

  // Helper for customer logo/initials
  const getCustomerLogo = () => {
    if (customerInfo?.logo) {
      return (
        <img
          src={customerInfo.logo}
          alt={customerInfo.companyName}
          className="w-12 h-12 rounded-lg object-cover"
        />
      );
    }
    // Fallback: initials
    const initials = (customerInfo?.companyName || customerInfo?.name || "C")
      .split(" ")
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
    return (
      <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-xl">
        {initials}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto backdrop-blur-sm">
      <div className="flex items-center justify-center min-h-screen p-2 lg:p-4">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-50"
          onClick={onClose}
        ></div>
        {/* Modal panel */}
        <div className="relative w-full max-w-[90vw] lg:max-w-[80vw] xl:max-w-[75vw] bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
          {/* Action Buttons (Download, Close) remain outside printable area */}
          {/* Printable Content */}
          <div id="po-pdf-content">
            {/* Header Section */}
            <div className="po-header bg-white dark:bg-gray-800 px-6 py-4 border-b-4 border-blue-600">
              <div className="flex justify-between items-start">
                {/* Left: Company Info */}
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-lg flex items-center justify-center shadow-lg">
                    <svg
                      className="w-7 h-7 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {companyInfo.name}
                    </h1>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                      {companyInfo.address}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {companyInfo.city}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      GSTIN:{" "}
                      <span className="font-mono">
                        {companyInfo.taxId ||
                          companyInfo.gstin ||
                          "27ABCDE1234F1Z5"}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Right: Document Info */}
                <div className="text-right">
                  <h2 className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                    PURCHASE ORDER
                  </h2>
                  <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-2 rounded-lg inline-block">
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      Document No:
                    </div>
                    <div className="text-lg font-bold text-blue-700 dark:text-blue-400 font-mono">
                      {formData.poNumber}
                    </div>
                  </div>
                  <div className="mt-2 space-y-1">
                    <div className="text-xs">
                      <span className="text-gray-600 dark:text-gray-400">
                        Customer:{" "}
                      </span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {customerInfo.companyName || customerInfo.name}
                      </span>
                    </div>
                    <div className="text-xs">
                      <span className="text-gray-600 dark:text-gray-400">
                        Status:{" "}
                      </span>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                          status === "draft"
                            ? "bg-yellow-100 text-yellow-800"
                            : status === "approved"
                            ? "bg-green-100 text-green-800"
                            : status === "submitted"
                            ? "bg-blue-100 text-blue-800"
                            : status === "rejected"
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* From/To Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 px-6 py-4 bg-gray-50 dark:bg-gray-900">
              {/* From (Customer) */}
              <div className="bg-white dark:bg-gray-800 border-2 border-blue-100 dark:border-blue-900 rounded-lg overflow-hidden">
                <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-2 border-b-2 border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <h3 className="text-sm font-bold text-blue-700 dark:text-blue-400 uppercase tracking-wide">
                      FROM (CUSTOMER)
                    </h3>
                  </div>
                </div>
                <div className="p-4">
                  <div className="space-y-2">
                    <div className="font-bold text-base text-gray-900 dark:text-white border-b pb-2">
                      {customerInfo.companyName || customerInfo.name}
                    </div>

                    {customerInfo.name &&
                      customerInfo.name !== customerInfo.companyName && (
                        <div className="text-sm">
                          <span className="text-gray-600 dark:text-gray-400">
                            Contact:
                          </span>{" "}
                          <span className="font-medium text-gray-900 dark:text-white">
                            {customerInfo.name}
                          </span>
                        </div>
                      )}

                    {customerInfo.phone && (
                      <div className="text-sm">
                        <span className="text-gray-600 dark:text-gray-400">
                          Phone:
                        </span>{" "}
                        <span className="font-medium text-gray-900 dark:text-white">
                          {customerInfo.phone}
                        </span>
                      </div>
                    )}

                    {customerInfo.email && (
                      <div className="text-sm">
                        <span className="text-gray-600 dark:text-gray-400">
                          Email:
                        </span>{" "}
                        <span className="font-medium text-gray-900 dark:text-white">
                          {customerInfo.email}
                        </span>
                      </div>
                    )}

                    {(customerInfo.billAddress ||
                      customerInfo.billAddressLine) && (
                      <div className="pt-2 border-t">
                        <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                          Billing Address:
                        </div>
                        <div className="text-sm text-gray-900 dark:text-white">
                          {customerInfo.billAddress ||
                            customerInfo.billAddressLine}
                        </div>
                        {(customerInfo.billCity || customerInfo.billState) && (
                          <div className="text-sm text-gray-900 dark:text-white">
                            {[
                              customerInfo.billCity,
                              customerInfo.billState,
                              customerInfo.billCountry,
                            ]
                              .filter(Boolean)
                              .join(", ")}
                            {customerInfo.billPinCode &&
                              ` - ${customerInfo.billPinCode}`}
                          </div>
                        )}
                      </div>
                    )}

                    {customerInfo.gstNumber && (
                      <div className="text-sm pt-2 border-t">
                        <span className="text-gray-600 dark:text-gray-400">
                          GSTIN:
                        </span>{" "}
                        <span className="font-mono font-bold text-gray-900 dark:text-white">
                          {customerInfo.gstNumber}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* To (Supplier) */}
              <div className="bg-white dark:bg-gray-800 border-2 border-indigo-100 dark:border-indigo-900 rounded-lg overflow-hidden">
                <div className="bg-indigo-50 dark:bg-indigo-900/20 px-4 py-2 border-b-2 border-indigo-200 dark:border-indigo-800">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    <h3 className="text-sm font-bold text-indigo-700 dark:text-indigo-400 uppercase tracking-wide">
                      TO (SUPPLIER)
                    </h3>
                  </div>
                </div>
                <div className="p-4">
                  <div className="space-y-2">
                    <div className="font-bold text-base text-gray-900 dark:text-white border-b pb-2">
                      {companyInfo.name}
                    </div>

                    <div className="text-sm text-gray-900 dark:text-white">
                      {companyInfo.address}
                    </div>
                    <div className="text-sm text-gray-900 dark:text-white">
                      {companyInfo.city}
                    </div>

                    <div className="pt-2 border-t grid grid-cols-2 gap-2">
                      <div className="text-sm">
                        <span className="text-gray-600 dark:text-gray-400">
                          Phone:
                        </span>{" "}
                        <span className="font-medium text-gray-900 dark:text-white">
                          {companyInfo.phone}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-600 dark:text-gray-400">
                          Email:
                        </span>{" "}
                        <span className="font-medium text-gray-900 dark:text-white">
                          {companyInfo.email}
                        </span>
                      </div>
                    </div>

                    <div className="text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        Website:
                      </span>{" "}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {companyInfo.website}
                      </span>
                    </div>

                    <div className="text-sm pt-2 border-t">
                      <span className="text-gray-600 dark:text-gray-400">
                        GSTIN:
                      </span>{" "}
                      <span className="font-mono font-bold text-gray-900 dark:text-white">
                        {companyInfo.taxId || companyInfo.gstin}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div className="px-6 py-4 bg-white dark:bg-gray-800">
              <div className="mb-3 flex items-center gap-2 pb-2 border-b-2 border-blue-600">
                <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide">
                  Order Items
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
                  <thead>
                    <tr className="bg-blue-600 text-white">
                      <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-xs font-bold text-center w-12">
                        #
                      </th>
                      <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-xs font-bold text-left">
                        Item Description
                      </th>
                      <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-xs font-bold text-center w-24">
                        Item Code
                      </th>
                      <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-xs font-bold text-center w-16">
                        Qty
                      </th>
                      <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-xs font-bold text-right w-24">
                        Unit Price
                      </th>
                      <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-xs font-bold text-center w-20">
                        GST Rate
                      </th>
                      <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-xs font-bold text-right w-24">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800">
                    {formData.items.map((item, index) => {
                      const gstRate =
                        item.gstPercent || item.product?.gstPercent || 18;
                      const lineSubtotal = item.unitPrice * item.quantity;
                      const gstAmount = (lineSubtotal * gstRate) / 100;
                      const lineTotal = lineSubtotal + gstAmount;

                      return (
                        <tr
                          key={item.id || index}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-center text-sm">
                            {index + 1}
                          </td>
                          <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm">
                            {item.name}
                          </td>
                          <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-center text-sm font-mono">
                            {item.sku || item.itemCode || "N/A"}
                          </td>
                          <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-center text-sm font-bold">
                            {item.quantity}
                          </td>
                          <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-right text-sm">
                            ₹{item.unitPrice.toFixed(2)}
                          </td>
                          <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-center text-sm font-semibold text-green-600">
                            {gstRate}%
                          </td>
                          <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-right text-sm font-bold">
                            ₹{lineTotal.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Bottom Section: Terms & Summary */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Left: Terms & Conditions */}
              <div className="lg:col-span-2">
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-600 p-4">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-blue-600">
                    <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <h3 className="font-bold text-blue-700 dark:text-blue-400 text-sm uppercase tracking-wide">
                      Terms & Conditions
                    </h3>
                  </div>
                  <div className="space-y-2 text-xs text-gray-700 dark:text-gray-300">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <span className="font-semibold">Required Date:</span>
                        <div className="text-gray-900 dark:text-white">
                          {formData.requiredDate || "Not specified"}
                        </div>
                      </div>
                      <div>
                        <span className="font-semibold">Payment Terms:</span>
                        <div className="text-gray-900 dark:text-white">
                          {formData.paymentTerms}
                        </div>
                      </div>
                      <div>
                        <span className="font-semibold">Shipping Method:</span>
                        <div className="text-gray-900 dark:text-white">
                          {formData.shippingMethod}
                        </div>
                      </div>
                    </div>
                    {formData.specialInstructions && (
                      <div className="pt-2 border-t">
                        <span className="font-semibold">
                          Special Instructions:
                        </span>
                        <div className="text-gray-900 dark:text-white mt-1">
                          {formData.specialInstructions}
                        </div>
                      </div>
                    )}
                    {formData.notes && (
                      <div className="pt-2">
                        <span className="font-semibold">Notes:</span>
                        <div className="text-gray-900 dark:text-white mt-1">
                          {formData.notes}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Authorization */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-600 p-4 mt-4">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-green-600">
                    <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <h3 className="font-bold text-green-700 dark:text-green-400 text-sm uppercase tracking-wide">
                      Authorization
                    </h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400 font-semibold">
                        Authorized By:
                      </span>
                      <div className="text-gray-900 dark:text-white font-medium mt-1">
                        {formData.authorizedBy}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400 font-semibold">
                        Authorization Date:
                      </span>
                      <div className="text-gray-900 dark:text-white font-medium mt-1">
                        {format(
                          new Date(formData.authorizedDate),
                          "dd-MM-yyyy"
                        )}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-600 dark:text-gray-400 font-semibold">
                        Signature/Initials:
                      </span>
                      <div className="border-t-2 border-gray-300 dark:border-gray-600 mt-2 pt-2">
                        <span className="text-gray-900 dark:text-white font-medium">
                          {formData.authorizedSignature || "_________________"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-blue-600 dark:bg-blue-700 rounded-lg border-2 border-blue-700 dark:border-blue-800 p-4 text-white sticky top-4">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-blue-400">
                    <CreditCard className="w-4 h-4" />
                    <h3 className="font-bold text-sm uppercase tracking-wide">
                      Order Summary
                    </h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-blue-100">
                        Subtotal (incl. GST):
                      </span>
                      <span className="font-bold text-lg">
                        ₹{formData.subtotal.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm border-t border-blue-400 pt-2">
                      <span className="text-blue-100">Total GST:</span>
                      <span className="font-semibold">
                        ₹{formData.taxAmount.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-blue-100 flex items-center gap-1">
                        <Truck className="w-3 h-3" />
                        Shipping Cost:
                      </span>
                      <span className="font-semibold">
                        ₹{formData.shippingCost.toFixed(2)}
                      </span>
                    </div>
                    {formData.discount > 0 && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-blue-100">Discount:</span>
                        <span className="font-semibold">
                          -₹{formData.discount.toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center pt-3 mt-3 border-t-2 border-blue-400">
                      <span className="font-bold text-base">Total:</span>
                      <span className="font-bold text-2xl bg-white text-blue-600 px-3 py-1 rounded-lg">
                        ₹{formData.total.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Sticky Footer Actions (Download, Close, Save, Submit) remain outside printable area */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 px-3 py-2 border-t border-gray-200 dark:border-gray-600 flex justify-between items-center sticky bottom-0 z-10 rounded-b-xl">
            <div className="text-xs">
              <div className="font-semibold text-gray-900 dark:text-white">
                Total Order Value:{" "}
                <span className="bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">
                  ₹{formData.total.toFixed(2)}
                </span>
              </div>
              {viewOnly && (
                <span className="text-blue-600 dark:text-blue-400">
                  Viewing: {formData.poNumber}
                </span>
              )}
              {draftOrder && !viewOnly && (
                <span className="text-blue-600 dark:text-blue-400">
                  Editing: {formData.poNumber}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleDownloadPDF}
                className="px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium text-xs shadow-sm hover:shadow-md transition-all flex items-center gap-1.5"
              >
                <Download className="w-3.5 h-3.5" />
                Download PDF
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs shadow-sm transition-all flex items-center gap-1.5"
              >
                <X className="w-3.5 h-3.5" />
                {viewOnly ? "Close" : "Cancel"}
              </button>
              {!viewOnly && (
                <>
                  <button
                    type="button"
                    onClick={handleSaveDraft}
                    className="px-3 py-1.5 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 font-medium text-xs shadow-sm hover:shadow-md transition-all flex items-center gap-1.5"
                  >
                    <Save className="w-3.5 h-3.5" />
                    {draftOrder ? "Update Draft" : "Save as Draft"}
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmPO}
                    className="px-3 py-1.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-green-500 font-medium text-xs shadow-sm hover:shadow-md transition-all flex items-center gap-1.5"
                  >
                    <CheckCircle className="w-3.5 h-3.5" />
                    {draftOrder
                      ? "Update Purchase Order"
                      : "Create Purchase Order"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderForm;
